(() => {
const FIXED_LINE_ITEM_PROPERTY_NAME = /^properties\[(.*)\]$/;

function isFixedLineItemPropertyObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeFixedLineItemPropertyToken(value) {
  return String(value || '').trim().toLowerCase();
}

function validateFixedLineItemPropertyKeys(value, allowedKeys, context) {
  const unknownKey = Object.keys(value).find((key) => !allowedKeys.has(key));
  if (unknownKey) throw new Error(`${context} contains an unsupported key: ${unknownKey}.`);
}

function normalizeFixedLineItemPropertyEntries(rawProperties, context) {
  if (!isFixedLineItemPropertyObject(rawProperties)) {
    throw new Error(`${context} must be an object of property labels and values.`);
  }

  const entries = [];
  const seenLabels = new Set();
  Object.entries(rawProperties).forEach(([rawLabel, rawValue]) => {
    const label = String(rawLabel || '').trim();
    const canonicalLabel = normalizeFixedLineItemPropertyToken(label);
    if (!label) throw new Error(`${context} contains a blank property label.`);
    if (label.startsWith('_')) throw new Error(`${context} property labels cannot begin with an underscore.`);
    if (seenLabels.has(canonicalLabel)) throw new Error(`${context} contains a duplicate property label: ${label}.`);
    if (typeof rawValue !== 'string' || !rawValue.trim()) {
      throw new Error(`${context}.${label} must be a nonblank string.`);
    }
    entries.push({ label, canonicalLabel, value: rawValue.trim() });
    seenLabels.add(canonicalLabel);
  });
  return entries;
}

function normalizeFixedLineItemProperties(rawPayload) {
  if (!isFixedLineItemPropertyObject(rawPayload)) throw new Error('The fixed-property payload is invalid.');
  const optionNames = Array.isArray(rawPayload.optionNames)
    ? rawPayload.optionNames.map((name) => String(name || '').trim())
    : [];
  const rawVariants = Array.isArray(rawPayload.variants) ? rawPayload.variants : [];
  if (optionNames.some((name) => !name) || rawVariants.length === 0) {
    throw new Error('Product option data is missing.');
  }

  const variants = new Map();
  const knownValues = optionNames.map(() => new Set());
  rawVariants.forEach((rawVariant) => {
    const options = Array.isArray(rawVariant.options) ? rawVariant.options.map((value) => String(value || '').trim()) : [];
    if (rawVariant.id === null || rawVariant.id === undefined || options.length !== optionNames.length) {
      throw new Error('A product variant has invalid option data.');
    }
    variants.set(String(rawVariant.id), options);
    options.forEach((value, index) => {
      if (value) knownValues[index].add(normalizeFixedLineItemPropertyToken(value));
    });
  });

  const configuration = rawPayload.configuration;
  if (!isFixedLineItemPropertyObject(configuration)) {
    throw new Error('Fixed line-item properties must be a JSON object.');
  }
  validateFixedLineItemPropertyKeys(configuration, new Set(['defaults', 'rules']), 'Fixed line-item properties');

  const defaults = configuration.defaults === undefined
    ? []
    : normalizeFixedLineItemPropertyEntries(configuration.defaults, 'defaults');
  if (configuration.rules !== undefined && !Array.isArray(configuration.rules)) {
    throw new Error('rules must be an array.');
  }

  const rules = (configuration.rules || []).map((rawRule, ruleIndex) => {
    const context = `rules[${ruleIndex}]`;
    if (!isFixedLineItemPropertyObject(rawRule)) throw new Error(`${context} must be an object.`);
    validateFixedLineItemPropertyKeys(rawRule, new Set(['when', 'properties']), context);
    if (!isFixedLineItemPropertyObject(rawRule.when) || Object.keys(rawRule.when).length === 0) {
      throw new Error(`${context}.when must contain at least one product option.`);
    }

    const usedOptionIndexes = new Set();
    const conditions = Object.entries(rawRule.when).map(([requestedName, requestedValues]) => {
      const canonicalName = normalizeFixedLineItemPropertyToken(requestedName);
      const optionIndex = optionNames.findIndex(
        (optionName) => normalizeFixedLineItemPropertyToken(optionName) === canonicalName
      );
      if (optionIndex === -1) throw new Error(`${context} references an unknown option: ${requestedName}.`);
      if (usedOptionIndexes.has(optionIndex)) throw new Error(`${context} repeats the option ${optionNames[optionIndex]}.`);

      const values = Array.isArray(requestedValues) ? requestedValues : [requestedValues];
      const normalizedValues = new Set();
      values.forEach((value) => {
        if (typeof value !== 'string' || !value.trim()) {
          throw new Error(`${context}.${requestedName} must contain nonblank strings.`);
        }
        const normalizedValue = normalizeFixedLineItemPropertyToken(value);
        if (!knownValues[optionIndex].has(normalizedValue)) {
          throw new Error(`${context} references an unknown ${optionNames[optionIndex]} value: ${value}.`);
        }
        normalizedValues.add(normalizedValue);
      });
      if (normalizedValues.size === 0) throw new Error(`${context}.${requestedName} needs at least one value.`);
      usedOptionIndexes.add(optionIndex);
      return { optionIndex, values: normalizedValues };
    });

    const properties = normalizeFixedLineItemPropertyEntries(rawRule.properties, `${context}.properties`);
    if (properties.length === 0) throw new Error(`${context}.properties cannot be empty.`);
    return { conditions, properties };
  });

  if (defaults.length === 0 && rules.length === 0) {
    throw new Error('Fixed line-item properties must contain defaults or rules.');
  }

  return { defaults, rules, variants };
}

function resolveFixedLineItemProperties(configuration, variantId) {
  const selectedOptions = configuration.variants.get(String(variantId));
  if (!selectedOptions) throw new Error(`Variant ${variantId} is missing from the fixed-property configuration.`);

  const resolved = new Map();
  const applyEntries = (entries) => {
    entries.forEach((entry) => {
      const existing = resolved.get(entry.canonicalLabel);
      if (existing) existing.value = entry.value;
      else resolved.set(entry.canonicalLabel, { ...entry });
    });
  };

  applyEntries(configuration.defaults);
  configuration.rules.forEach((rule) => {
    const matches = rule.conditions.every((condition) =>
      condition.values.has(normalizeFixedLineItemPropertyToken(selectedOptions[condition.optionIndex]))
    );
    if (matches) applyEntries(rule.properties);
  });
  return [...resolved.values()];
}

function hasNonblankFixedLineItemPropertyValue(value) {
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof File !== 'undefined' && value instanceof File) return value.size > 0;
  return value !== null && value !== undefined;
}

function mergeFixedLineItemProperties(formData, fixedProperties) {
  if (fixedProperties.length === 0) return;
  const propertyEntries = Array.from(formData.entries()).filter(([name]) =>
    FIXED_LINE_ITEM_PROPERTY_NAME.test(String(name || ''))
  );
  const fixedLabels = new Set(fixedProperties.map((property) => property.canonicalLabel));
  const customerValues = new Map();

  propertyEntries.forEach(([name, value]) => {
    const match = String(name || '').match(FIXED_LINE_ITEM_PROPERTY_NAME);
    const canonicalLabel = normalizeFixedLineItemPropertyToken(match ? match[1] : '');
    if (fixedLabels.has(canonicalLabel) && hasNonblankFixedLineItemPropertyValue(value)) {
      customerValues.set(canonicalLabel, value);
    }
  });

  new Set(propertyEntries.map(([name]) => name)).forEach((name) => formData.delete(name));
  fixedProperties.forEach((property) => {
    const value = customerValues.has(property.canonicalLabel)
      ? customerValues.get(property.canonicalLabel)
      : property.value;
    formData.append(`properties[${property.label}]`, value);
  });
  propertyEntries.forEach(([name, value]) => {
    const match = String(name || '').match(FIXED_LINE_ITEM_PROPERTY_NAME);
    const canonicalLabel = normalizeFixedLineItemPropertyToken(match ? match[1] : '');
    if (!fixedLabels.has(canonicalLabel)) formData.append(name, value);
  });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.fixedLineItemProperties = this.loadFixedLineItemProperties();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        let fixedProperties = [];
        if (this.fixedLineItemProperties) {
          try {
            if (this.fixedLineItemProperties.error) throw this.fixedLineItemProperties.error;
            fixedProperties = resolveFixedLineItemProperties(
              this.fixedLineItemProperties.configuration,
              this.variantIdInput.value
            );
          } catch (error) {
            console.error('Invalid fixed line-item properties configuration', error);
            this.handleErrorMessage(
              this.fixedLineItemProperties.setupError || window.variantStrings?.unavailable
            );
            return;
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        mergeFixedLineItemProperties(formData, fixedProperties);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      loadFixedLineItemProperties() {
        const configNode = this.form.querySelector('[data-fixed-line-item-properties]');
        if (!configNode) return null;

        let payload;
        try {
          payload = JSON.parse(configNode.textContent);
          return {
            configuration: normalizeFixedLineItemProperties(payload),
            setupError: String(payload.setupError || ''),
            error: null,
          };
        } catch (error) {
          return {
            configuration: null,
            setupError: String(payload?.setupError || ''),
            error,
          };
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
})();
